import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { mapValues, orderBy, uniqBy } from 'lodash'
import { nbPerPageLabel, SANDRE } from '../../constants/ReferencialConstants'
import { push } from '@lagunovsky/redux-react-router'
import { setLocalStorageJson } from '../../../utils/FormUtils'
import Select from '../../../components/forms/Select'
import { getDate } from '../../../utils/DateUtil'
import { getSandreLabel, searchAllCharacters } from '../../../utils/StringUtil'
import { getStatusInformation } from '../../util/StatusUtils'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import ReferencialAction from 'referencial/action/ReferencialAction'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import Input from 'components/forms/Input'
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import { hasValue } from 'utils/NumberUtil'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'
import { PATH_REFERENCIAL, PATH_REFERENCIAL_RPQS_LINK } from 'home/constants/RouteConstants'
import { getReferencialCategories } from 'referencial/util/ReferencialUtils'

const headers = ['field', 'code', 'name', 'mnemonique', 'category', 'displayStatus', 'reference']
const exportHeaders = ['field', 'code', 'name', 'mnemonique', 'category', 'status', 'reference', 'comment', 'creationDate', 'updateDate', 'author']

const LexiconsApp = ({ rpqs }) => {
    const {
        sandreCodes,
        referencialSandreCodes,
        referencialStatus,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        referencialSandreCodes: store.ReferencialReducer.referencialSandreCodes,
        referencialStatus: store.ReferencialReducer.referencialStatus,
    }), shallowEqual)

    const [filter, setFilter] = useLocalStorage('LEXICON_FILTER', {})
    const [filterTmp, setFilterTmp] = useLocalStorage('LEXICON_FILTER', {})

    const dispatch = useDispatch()

    useEffect(() => {
        if (!referencialStatus.length) {
            dispatch(ReferencialAction.fetchReferencialStatus())
        }
    }, [])

    useEffect(() => {
        if (rpqs) {
            setFilter(prev => ({ ...prev, category: i18n.rpqs }))
            setFilterTmp(prev => ({ ...prev, category: i18n.rpqs }))
        }
    }, [rpqs, setFilter, setFilterTmp])

    useTitle(() => {
        if (rpqs) {
            return [{
                title: i18n.rpqs,
                href: 'rpqs',
            }, {
                title: i18n.RPQSReferencials,
                href: PATH_REFERENCIAL_RPQS_LINK,
            }, {
                title: i18n.lexicons,
                href: `${PATH_REFERENCIAL_RPQS_LINK}lexicons`,
            }]
        }
        return [{
            title: i18n.referencials,
            href: PATH_REFERENCIAL,
        }, {
            title: i18n.lexicons,
            href: `${PATH_REFERENCIAL}/lexicons`,
        }]
    }, [rpqs])

    const filteredList = useMemo(() => filter.selectedField ? sandreCodes.filter(sandreCode => sandreCode.field === filter.selectedField) : sandreCodes, [filter.selectedField, sandreCodes])
    const refList = useMemo(() => filteredList.filter(sandreCode => !referencialSandreCodes.some(rsf => rsf.code && rsf.field === sandreCode.field)), [filteredList, referencialSandreCodes])
    const lexiconByStatus = useMemo(() => hasValue(filter.status) ? refList.filter(lex => lex.status === filter.status) : refList, [filter.status, refList])
    const lexiconByCategory = useMemo(() => hasValue(filter.category) ? lexiconByStatus.filter(lex => lex.category === filter.category) : lexiconByStatus, [filter.category, lexiconByStatus])
    const dataLexicons = useMemo(() => orderBy(lexiconByCategory, ['field', 'code']).map(sandreCode => ({
        ...sandreCode,
        updateDate: getDate(sandreCode.updateDate),
        creationDate: getDate(sandreCode.date),
        code: `${sandreCode.code}`,
        displayStatus: sandreCode.status === 3 ? getStatusInformation('3') : getSandreLabel(sandreCodes, SANDRE.DISPOSITIFS_ETATS, sandreCode.status),
        status: getSandreLabel(sandreCodes, SANDRE.DISPOSITIFS_ETATS, sandreCode.status),
        headers: exportHeaders,
        category: sandreCode.category || '',
    })), [lexiconByCategory, sandreCodes])

    const data = useMemo(() => {
        return dataLexicons
            .filter((obj) => searchAllCharacters(headers.map((key) => obj[key]).join())
                .includes(searchAllCharacters(filter.searchValue || '')))
    }, [dataLexicons, filter])

    const colorData = useMemo(() => data.map(u => ({ ...mapValues(u, o => ({ value: o })), fieldLink: u.field, id: u.code })), [data])

    useActions(() => {
        return {
            new: () => dispatch(push(rpqs ? `${PATH_REFERENCIAL_RPQS_LINK}lexicon/new/-1/dashboard` : '/referencial/lexicon/new/-1/dashboard')),
            referencialActions: {
                actions: {
                    export: {
                        data,
                        titleFile: i18n.lexicons,
                    },
                },
            },
        }
    }, [filter, rpqs])

    useEffect(() => {
        dispatch(ReferencialAction.fetchSandreCodes())
        dispatch(ReferencialAction.fetchReferencialSandreCodes())
    }, [])

    const onFilterChange = (newFilter) => {
        setLocalStorageJson('LEXICON_FILTER', newFilter)
        setFilterTmp(newFilter)
    }

    const filteredLexicons = useMemo(() => {
        const lexicons = sandreCodes.filter(sandreCode => !referencialSandreCodes.some(rsf => rsf.code && rsf.field === sandreCode.field))
        return orderBy(
            uniqBy(hasValue(filterTmp.category)
                ? lexicons.filter(sandreCode => sandreCode.category === filterTmp.category)
                : lexicons, 'field'),
            'field',
        )
    }, [filterTmp.category, referencialSandreCodes, sandreCodes])

    return (
        <Stack spacing={1}>
            <Card>
                <CardTitle title={i18n.criterias} color='bannerColor'/>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={3}>
                            <Input
                                title={i18n.search}
                                value={filterTmp.searchValue}
                                onChange={v => setFilterTmp({ ...filterTmp, searchValue: v })}
                                onEnterKeyPress={() => setFilter({ ...filterTmp })}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                options={referencialStatus}
                                label={i18n.status}
                                onChange={v => setFilterTmp({ ...filterTmp, status: v })}
                                value={filterTmp.status}
                                nullLabel='&nbsp;'
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                options={getReferencialCategories()}
                                label={i18n.category}
                                onChange={v => setFilterTmp({ ...filterTmp, category: v })}
                                value={filterTmp.category}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                label={i18n.lexicons}
                                value={filterTmp.selectedField}
                                options={filteredLexicons}
                                onChange={v => onFilterChange({ ...filterTmp, selectedField: v })}
                                returnNull={true}
                                clearFunction
                                keyValue='field'
                                keyLabel='field'
                            />
                        </Grid>
                        <Grid container size={12} columnSpacing={1} justifyContent='flex-end'>
                            <Button
                                variant='outlined'
                                onClick={() => {
                                    setFilter({})
                                    setFilterTmp({})
                                }}
                            >
                                {i18n.reinit}
                            </Button>
                            <Button
                                variant='contained'
                                onClick={() => setFilter({ ...filterTmp })}
                            >
                                {i18n.search}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <CardTable
                title={i18n.nonSandreLexicons}
                rowsPerPageOptions={nbPerPageLabel}
                headersLabel={{ displayStatus: i18n.status }}
                rows={colorData}
                headers={headers}
                color='bannerColor'
                onClickRow={element => dispatch(push(rpqs ?
                    `${PATH_REFERENCIAL_RPQS_LINK}lexicon/${element.fieldLink}/${element.id}/dashboard`
                    : `/referencial/lexicon/${element.fieldLink}/${element.id}/dashboard`,
                ))}
            />
        </Stack>
    )
}

LexiconsApp.propTypes = {
    rpqs: PropTypes.bool,
}

export default LexiconsApp